/**
 * Structured inventory rows for AI Lead Intake HITL.
 * Keeps itemsText / Accept-compatible item payloads in sync with row edits.
 */

export const INVENTORY_TAG_OPTIONS = Object.freeze([
  "Fragile",
  "Large",
  "Dismantle",
]);

/**
 * @returns {{ id: string, name: string, quantity: number, details: string, tags: string[] }}
 */
export function createEmptyInventoryRow() {
  return {
    id: newInventoryRowId(),
    name: "",
    quantity: 1,
    details: "",
    tags: [],
  };
}

/**
 * @returns {string}
 */
function newInventoryRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Parse a free-text inventory line into name / qty / details.
 * Supports: "Boxes x6", "6x Boxes", "2 queen beds", "Fridge (Defy)".
 * @param {string} line
 * @returns {{ name: string, quantity: number, details: string }}
 */
export function parseInventoryLine(line) {
  let raw = String(line ?? "").trim();
  if (!raw) {
    return { name: "", quantity: 1, details: "" };
  }

  let details = "";
  const parenMatch = raw.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  if (parenMatch) {
    raw = parenMatch[1].trim();
    details = parenMatch[2].trim();
  }

  let quantity = 1;
  let name = raw;

  const trailingQty = raw.match(/^(.*?)\s*[x×]\s*(\d+)\s*$/i);
  const leadingQtyX = raw.match(/^(\d+)\s*[x×]\s*(.+)$/i);
  const leadingQtySpace = raw.match(/^(\d+)\s+(.+)$/);

  if (trailingQty) {
    name = trailingQty[1].trim();
    quantity = Math.max(1, parseInt(trailingQty[2], 10) || 1);
  } else if (leadingQtyX) {
    quantity = Math.max(1, parseInt(leadingQtyX[1], 10) || 1);
    name = leadingQtyX[2].trim();
  } else if (leadingQtySpace) {
    quantity = Math.max(1, parseInt(leadingQtySpace[1], 10) || 1);
    name = leadingQtySpace[2].trim();
  }

  return { name, quantity, details };
}

/**
 * @param {{ name?: string, quantity?: number, details?: string, tags?: string[] }} row
 * @returns {string}
 */
export function formatInventoryRowAsText(row = {}) {
  const name = String(row.name || "").trim();
  if (!name) return "";
  const qty = Math.max(1, Number(row.quantity) || 1);
  const details = String(row.details || "").trim();
  const tags = Array.isArray(row.tags)
    ? row.tags.map((t) => String(t).trim()).filter(Boolean)
    : [];

  let text = qty > 1 ? `${qty}x ${name}` : name;
  if (details) text += ` (${details})`;
  if (tags.length) text += ` [${tags.join(", ")}]`;
  return text;
}

/**
 * Strip trailing [tag, tag] markers from a line and return tags.
 * @param {string} line
 * @returns {{ line: string, tags: string[] }}
 */
function extractTrailingTags(line) {
  const match = String(line ?? "").match(/^(.*?)\s*\[([^\]]+)\]\s*$/);
  if (!match) return { line: String(line ?? "").trim(), tags: [] };
  const tags = match[2]
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .filter((t) =>
      INVENTORY_TAG_OPTIONS.some((opt) => opt.toLowerCase() === t.toLowerCase())
    )
    .map((t) => {
      const known = INVENTORY_TAG_OPTIONS.find(
        (opt) => opt.toLowerCase() === t.toLowerCase()
      );
      return known || t;
    });
  return { line: match[1].trim(), tags };
}

/**
 * Build structured rows from draft/prefill fields.
 * Prefers existing inventoryRows, then object items, then itemsText.
 * @param {{
 *   inventoryRows?: unknown,
 *   items?: unknown,
 *   itemsText?: unknown,
 *   itemsArray?: unknown,
 * }} [input]
 * @returns {Array<{ id: string, name: string, quantity: number, details: string, tags: string[] }>}
 */
export function parseInventoryRowsFromFields(input = {}) {
  const { inventoryRows, items, itemsText, itemsArray } = input;

  if (Array.isArray(inventoryRows) && inventoryRows.length > 0) {
    return inventoryRows
      .map((row) => normalizeInventoryRow(row))
      .filter((row) => row.name);
  }

  const list = Array.isArray(itemsArray)
    ? itemsArray
    : Array.isArray(items)
      ? items
      : null;

  if (list?.length) {
    const fromObjects = list
      .map((entry) => {
        if (typeof entry === "string") {
          const { line, tags } = extractTrailingTags(entry);
          const parsed = parseInventoryLine(line);
          return normalizeInventoryRow({ ...parsed, tags });
        }
        if (entry && typeof entry === "object") {
          const name =
            String(entry.name || entry.content || entry.description || "").trim();
          if (!name && entry.type && entry.type !== "text") return null;
          if (!name) return null;
          const qty =
            entry.quantity != null && entry.quantity !== ""
              ? Math.max(1, Number(entry.quantity) || 1)
              : null;
          // If content already encodes qty ("6x Boxes"), prefer object fields when present
          if (
            entry.name ||
            entry.details != null ||
            entry.tags != null ||
            qty != null
          ) {
            return normalizeInventoryRow({
              id: entry.id,
              name: entry.name || name,
              quantity: qty != null ? qty : 1,
              details: entry.details || "",
              tags: entry.tags,
            });
          }
          const { line, tags } = extractTrailingTags(name);
          const parsed = parseInventoryLine(line);
          return normalizeInventoryRow({ ...parsed, tags, id: entry.id });
        }
        return null;
      })
      .filter(Boolean);
    if (fromObjects.length) return fromObjects;
  }

  const text = String(itemsText ?? "").trim();
  if (!text) return [];

  return text
    .split(/[\n,]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const { line, tags } = extractTrailingTags(part);
      const parsed = parseInventoryLine(line);
      return normalizeInventoryRow({ ...parsed, tags });
    })
    .filter((row) => row.name);
}

/**
 * @param {unknown} row
 * @returns {{ id: string, name: string, quantity: number, details: string, tags: string[] }}
 */
export function normalizeInventoryRow(row = {}) {
  const tags = Array.isArray(row?.tags)
    ? row.tags
        .map((t) => String(t).trim())
        .filter(Boolean)
        .map((t) => {
          const known = INVENTORY_TAG_OPTIONS.find(
            (opt) => opt.toLowerCase() === t.toLowerCase()
          );
          return known || t;
        })
    : [];
  return {
    id: String(row?.id || newInventoryRowId()),
    name: String(row?.name || "").trim(),
    quantity: Math.max(1, Number(row?.quantity) || 1),
    details: String(row?.details || "").trim(),
    tags,
  };
}

/**
 * @param {Array} rows
 * @returns {string}
 */
export function serializeInventoryRowsToItemsText(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => formatInventoryRowAsText(normalizeInventoryRow(row)))
    .filter(Boolean)
    .join("\n");
}

/**
 * Lead/Accept-compatible item objects (type/content/quantity + details/tags).
 * @param {Array} rows
 * @returns {Array<{ type: string, content: string, quantity: number, details?: string, tags?: string[], name?: string }>}
 */
export function serializeInventoryRowsToItems(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => normalizeInventoryRow(row))
    .filter((row) => row.name)
    .map((row) => {
      const content = formatInventoryRowAsText(row);
      return {
        type: "text",
        name: row.name,
        content,
        quantity: row.quantity,
        ...(row.details ? { details: row.details } : {}),
        ...(row.tags.length ? { tags: row.tags } : {}),
      };
    });
}

/**
 * True when any row has a non-empty name.
 * @param {unknown} rows
 * @param {unknown} itemsText
 * @returns {boolean}
 */
export function hasInventoryContent(rows, itemsText) {
  if (Array.isArray(rows) && rows.some((r) => String(r?.name || "").trim())) {
    return true;
  }
  return Boolean(String(itemsText || "").trim());
}
